import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type {
  ContratacaoPreviewResponse,
  PlanoPublico,
} from '@/api/contratacao.actions.schema';
import type { CheckoutOrderBumpDef } from '@/config/checkout-order-bumps';
import { CHECKOUT_MARKETING_CLASSES } from '@/lib/checkout-theme';
import { ArrowRightLeft, Check } from 'lucide-react';

type Props = {
  plano: PlanoPublico;
  selectedCiclo: 'mensal' | 'anual';
  onSelectCiclo: (c: 'mensal' | 'anual') => void;
  skipCicloChoice: boolean;
  urlCiclo?: 'mensal' | 'anual';
  isTrial: boolean;
  trialDays?: number;
  trialCardRequired?: boolean;
  descontoPct: number;
  descontoValor: number;
  formatPrice: (v: number | string) => string;
  precoMensalAnual: number | null;
  cupom: string;
  onCupomChange: (v: string) => void;
  cupomFromUrl?: string;
  bumps: CheckoutOrderBumpDef[];
  selectedBumpPriceIds: string[];
  onToggleBump: (stripePriceId: string, selected: boolean) => void;
  onAlterarImobiliaria: () => void;
  nomeImobiliaria: string;
  siteAtual: string;
  planosDisponiveis: PlanoPublico[];
  currentPlanoSlug?: string;
  isLoadingPlanosDisponiveis?: boolean;
  onSelecionarPlano: (slug: string) => void;
  pricingPreview?: ContratacaoPreviewResponse['data'] | null;
  isPricingPreviewLoading?: boolean;
};

const toNumber = (v: number | string | null | undefined): number => {
  if (v == null) return 0;
  return typeof v === 'string' ? parseFloat(v) : v;
};

function bumpInitials(titulo: string): string {
  const parts = titulo.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function imobInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'ID';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function ContratacaoCheckoutReview({
  plano,
  selectedCiclo,
  onSelectCiclo,
  skipCicloChoice,
  urlCiclo,
  isTrial,
  trialDays,
  descontoPct,
  formatPrice,
  precoMensalAnual,
  cupom,
  onCupomChange,
  cupomFromUrl,
  bumps,
  selectedBumpPriceIds,
  onToggleBump,
  onAlterarImobiliaria,
  nomeImobiliaria,
  planosDisponiveis,
  currentPlanoSlug,
  isLoadingPlanosDisponiveis = false,
  onSelecionarPlano,
  pricingPreview,
  isPricingPreviewLoading = false,
}: Props) {
  const precoMensal = toNumber(plano.precoMensal);
  const precoAnual = toNumber(plano.precoAnual);
  const hasAnnual = plano.precoAnual != null && precoAnual > 0;
  const precoCiclo = selectedCiclo === 'anual' && hasAnnual ? precoAnual : precoMensal;
  const cycleLabelText = selectedCiclo === 'anual' ? 'Anual' : 'Mensal';

  const trialBadgeText =
    isTrial && trialDays != null && trialDays > 0
      ? `${trialDays} DIA${trialDays !== 1 ? 'S' : ''} GRÁTIS`
      : null;

  const cupomTrim = cupom.trim();
  const cupomFromUrlTrim = cupomFromUrl?.trim();
  const subtotal = pricingPreview?.subtotal ?? precoCiclo;
  const desconto = pricingPreview?.discount ?? 0;
  const total = pricingPreview?.total ?? Math.max(0, subtotal - desconto);
  const cupomPreview = pricingPreview?.coupon;
  const cupomInvalido = Boolean(cupomTrim) && Boolean(cupomPreview && !cupomPreview.valid);
  const cupomValido = Boolean(cupomTrim) && Boolean(cupomPreview?.valid);

  return (
    <div className="summary">
      <div className={cn('sum-head', isTrial && 'sum-head--trial')}>
        {trialBadgeText ? <span className="trial-corner">{trialBadgeText}</span> : null}
        <div className="sum-head-row">
          <div>
            <h3 className="plan-name">Plano {plano.nome}</h3>
            <p className="plan-tag">
              Ciclo: <strong>{cycleLabelText}</strong>
              {skipCicloChoice && urlCiclo && !isTrial ? <span> (definido pelo link)</span> : null}
            </p>
          </div>
          <div className="price-tower">
            {isTrial ? (
              <>
                <div className="price-now">
                  R$ 0
                  <span style={{ fontSize: 14, color: '#64748b', fontWeight: 500 }}>
                    {' '}/ {trialDays} dia{trialDays !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="price-cycle">depois {formatPrice(precoCiclo)}{selectedCiclo === 'anual' ? '/ano' : '/mês'}</div>
              </>
            ) : (
              <>
                <div className="price-now">{formatPrice(precoCiclo)}</div>
                <div className="price-cycle">{selectedCiclo === 'anual' ? 'cobrança anual' : 'por mês'}</div>
                {precoMensalAnual != null && selectedCiclo === 'mensal' ? (
                  <div className="price-strike">{formatPrice(precoMensalAnual)}/mês no anual</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Trocar plano */}
      {planosDisponiveis.length > 1 ? (
        <div className="sum-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="sum-label" style={{ marginBottom: 0 }}>Plano selecionado</span>
          <Dialog>
            <DialogTrigger asChild>
              <button type="button" className="sum-link">
                <ArrowRightLeft className="h-3.5 w-3.5" />
                Trocar de plano
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl overflow-hidden border-slate-200 bg-gradient-to-b from-white to-slate-50/80 p-0">
              <DialogHeader className={cn('border-b border-slate-200 px-6 py-5', CHECKOUT_MARKETING_CLASSES.cardLightIndigo)}>
                <DialogTitle className="flex items-center gap-2 text-slate-900">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                    <ArrowRightLeft className="h-4 w-4" />
                  </span>
                  Escolha outro plano
                </DialogTitle>
                <p className="text-xs text-slate-600">Compare os valores e selecione o plano ideal para continuar.</p>
              </DialogHeader>
              <div className="grid gap-3 max-h-[60vh] overflow-auto px-6 py-5">
                {isLoadingPlanosDisponiveis ? (
                  <p className="text-sm text-slate-500">Carregando planos...</p>
                ) : planosDisponiveis.length === 0 ? (
                  <p className="text-sm text-slate-500">Nenhum plano disponível para troca no momento.</p>
                ) : (
                  planosDisponiveis.map((item) => {
                    const isCurrent = item.slug === currentPlanoSlug;
                    return (
                      <div
                        key={item.id}
                        className={cn(
                          'rounded-xl border p-4 transition-all duration-200',
                          isCurrent
                            ? cn(
                                'border-indigo-300 shadow-sm',
                                CHECKOUT_MARKETING_CLASSES.cardLightIndigo,
                              )
                            : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-sm',
                        )}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900">Plano {item.nome}</p>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                              <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                                Mensal: {formatPrice(item.precoMensal)}
                              </Badge>
                              {item.precoAnual != null ? (
                                <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100">
                                  Anual: {formatPrice(item.precoAnual)}
                                </Badge>
                              ) : null}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant={isCurrent ? 'secondary' : 'outline'}
                            size="sm"
                            disabled={isCurrent || !item.slug}
                            onClick={() => item.slug && onSelecionarPlano(item.slug)}
                            className={cn(
                              !isCurrent &&
                                'border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800',
                            )}
                          >
                            {isCurrent ? 'Plano atual' : 'Selecionar'}
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      ) : null}

      {/* Ciclo de cobrança */}
      {!skipCicloChoice && hasAnnual ? (
        <div className="sum-section">
          <div className="sum-label">Ciclo de cobrança</div>
          <div className="cycle">
            <button
              type="button"
              className={cn(selectedCiclo === 'mensal' && 'is-active')}
              onClick={() => onSelectCiclo('mensal')}
            >
              Mensal
              <span className="cyc-cost">{formatPrice(precoMensal)}/mês</span>
            </button>
            <button
              type="button"
              className={cn(selectedCiclo === 'anual' && 'is-active')}
              onClick={() => onSelectCiclo('anual')}
            >
              {descontoPct > 0 ? <span className="cyc-save">−{descontoPct}%</span> : null}
              Anual
              <span className="cyc-cost">
                {precoMensalAnual != null ? `${formatPrice(precoMensalAnual)}/mês` : formatPrice(precoAnual)}
              </span>
            </button>
          </div>
        </div>
      ) : null}

      {/* Bumps */}
      {bumps.length > 0 ? (
        <div className="sum-section">
          <div className="sum-label">Adicione ao seu plano (opcional)</div>
          {bumps.map((b) => {
            const on = selectedBumpPriceIds.includes(b.stripePriceId);
            return (
              <div
                key={b.id}
                className={cn('bump', on && 'is-on')}
                role="checkbox"
                aria-checked={on}
                tabIndex={0}
                onClick={() => onToggleBump(b.stripePriceId, !on)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    onToggleBump(b.stripePriceId, !on);
                  }
                }}
              >
                <div className="bump-check">
                  <Check />
                </div>
                <div className="bump-thumb">{bumpInitials(b.titulo)}</div>
                <div className="bump-body">
                  <h4 className="bump-title-text">{b.titulo}</h4>
                  <div className="bump-price-line">
                    <span className="bump-price">{b.precoLabel}</span>
                  </div>
                  <p className="bump-desc">{b.descricao}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Cupom */}
      <div className="sum-section">
        <div className="sum-label">Cupom de desconto</div>
        <div className="coupon-row">
          <input
            className="input-xl"
            placeholder="Ex: DESCONTO10"
            value={cupom}
            onChange={(e) => onCupomChange(e.target.value)}
            autoComplete="off"
          />
        </div>
        {cupomFromUrlTrim && cupomTrim.toUpperCase() === cupomFromUrlTrim.toUpperCase() ? (
          <div className="coupon-applied">
            <Check />
            Cupom <strong>{cupomFromUrlTrim.toUpperCase()}</strong> aplicado pelo link
          </div>
        ) : cupomFromUrlTrim && !cupomTrim ? (
          <p className="field-hint">
            Código do link: <span className="font-mono font-semibold">{cupomFromUrlTrim}</span> — você pode alterar acima.
          </p>
        ) : null}
        {isPricingPreviewLoading && cupomTrim ? (
          <p className="field-hint">Validando cupom...</p>
        ) : null}
        {cupomValido ? (
          <div className="coupon-applied">
            <Check />
            Cupom <strong>{cupomPreview?.code.toUpperCase()}</strong> aplicado
            {desconto > 0 ? ` (${formatPrice(desconto)} de desconto)` : ''}
          </div>
        ) : null}
        {cupomInvalido ? (
          <p className="field-error">{cupomPreview?.reason || 'Cupom inválido.'}</p>
        ) : null}
      </div>

      {/* Imobiliária */}
      <div className="sum-section">
        <div className="sum-label">Imobiliária</div>
        <div className="imob-block">
          <div className="imob-logo">{imobInitials(nomeImobiliaria || 'ImobDesk')}</div>
          <div className="min-w-0 flex-1">
            <p className="imob-name">{nomeImobiliaria.trim() || 'Nome da imobiliária não informado'}</p>
            <p className="imob-meta">
              {nomeImobiliaria.trim()
                ? 'Esse nome aparecerá em sua conta'
                : 'Opcional para esta etapa'}
            </p>
          </div>
          <button type="button" className="imob-edit" onClick={onAlterarImobiliaria}>
            {nomeImobiliaria.trim() ? 'Alterar' : 'Definir'}
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="totals">
        {isTrial ? (
          <>
            <div className="tot-row">
              <span>Plano {plano.nome} (após teste)</span>
              <span>{formatPrice(total)}{selectedCiclo === 'anual' ? '/ano' : '/mês'}</span>
            </div>
            {desconto > 0 ? (
              <div className="tot-row">
                <span>Desconto{cupomPreview?.code ? ` (${cupomPreview.code.toUpperCase()})` : ''}</span>
                <span>-{formatPrice(desconto)}</span>
              </div>
            ) : null}
            <div className="tot-row tot-grand">
              <span>Cobrado hoje</span>
              <span className="price-now" style={{ color: '#059669' }}>R$ 0</span>
            </div>
          </>
        ) : (
          <>
            <div className="tot-row">
              <span>Plano {plano.nome} ({cycleLabelText.toLowerCase()})</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            {desconto > 0 ? (
              <div className="tot-row">
                <span>Desconto{cupomPreview?.code ? ` (${cupomPreview.code.toUpperCase()})` : ''}</span>
                <span>-{formatPrice(desconto)}</span>
              </div>
            ) : null}
            <div className="tot-row tot-grand">
              <span>Total hoje</span>
              <span className="price-now">{formatPrice(total)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
